import { ThemedText } from '@/components/themed-text';
import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { Disclosure } from './disclosure';
import { SectionTitle, SeoSection } from './seo-blocks';
import { useThemedHex } from '@/hooks/use-theme-colors';

export interface FaqItem {
  question: string;
  answer: string;
}

/** JSON-LD `FAQPage` a partir da mesma lista que aparece na tela. */
export const faqJsonLd = (items: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

/**
 * Perguntas em linhas que abrem ao toque. A pergunta é h3 de verdade e a
 * resposta fica sempre no DOM (ver `Disclosure`).
 */
export function FaqList({ items }: { items: FaqItem[] }) {
  const th = useThemedHex();
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (index: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <View>
      {items.map((item, index) => {
        const isOpen = open.has(index);
        return (
          <View key={item.question} className="border-b border-neutral-200">
            <Pressable
              onPress={() => toggle(index)}
              accessibilityRole="button"
              accessibilityState={{ expanded: isOpen }}
              className="flex-row items-center justify-between gap-4 py-5"
            >
              <ThemedText role="heading" aria-level={3} className="flex-1 font-semibold text-base">
                {item.question}
              </ThemedText>
              <Ionicons name={isOpen ? 'remove' : 'add'} size={20} color={isOpen ? '#FD5001' : th('#A3A3A3')} />
            </Pressable>
            <Disclosure open={isOpen}>
              <ThemedText className="pb-5 pr-8 text-base text-neutral-600">{item.answer}</ThemedText>
            </Disclosure>
          </View>
        );
      })}
    </View>
  );
}

/** Seção FAQ padrão, com o bloco "veja também" logo abaixo. */
export function FaqSection({
  items,
  footer,
  title = 'Perguntas frequentes',
}: {
  items: FaqItem[];
  footer?: React.ReactNode;
  /** Título da seção (nas páginas por ramo, uma pergunta do ramo). */
  title?: string;
}) {
  return (
    <SeoSection tone="white" narrow>
      <SectionTitle eyebrow="Dúvidas" title={title} />
      <FaqList items={items} />
      {footer}
    </SeoSection>
  );
}
